import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import settingsSlice, { integrationTrustpilotSelector } from '@/redux/features/settings.slice';
import {
  useCreateIntegrationTrustpilotMutation,
  useDeleteIntegrationTrustpilotMutation,
  useTestConnectionIntegrationTrustpilotMutation,
} from '@/redux/api/api.caller';
import { handleToastMutation } from '@/helpers/toast';
import { getIntegrationErrorMessage } from '../helpers';
import commonSlice from '@/redux/features/common.slice';
import { IIntegrationTrustpilot } from '@/types/settings';
import { IntegrationAdapter, IntegrationStatus } from '../types';

const validateIdentifierField = (value: string): string => {
  if (!value) return 'Field is required';
  if (!new RegExp('^[a-zA-Z0-9_-]*$').test(value)) return 'Field is invalid';
  return '';
};

const validateOptionalIdentifierField = (value: string): string => {
  if (!value) return '';
  if (!new RegExp('^[a-zA-Z0-9_-]*$').test(value)) return 'Field is invalid';
  return '';
};

const validateSecretField = (value: string): string => (value ? '' : 'Field is required');

const useTrustpilotIntegrationAdapter = (status?: IIntegrationTrustpilot, isFetching = false): IntegrationAdapter => {
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState<{ create: string; delete: string; test?: string }>({ create: '', delete: '' });
  const [testConnectionResult, setTestConnectionResult] = useState<{ businessUnitId: string } | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const trustpilotState = useSelector(integrationTrustpilotSelector);
  const [createTrustpilot, createdTrustpilotData] = useCreateIntegrationTrustpilotMutation();
  const [deleteTrustpilot, deletedTrustpilotData] = useDeleteIntegrationTrustpilotMutation();
  const [testConnection, testedConnectionData] = useTestConnectionIntegrationTrustpilotMutation();

  const integrationStatus: IntegrationStatus = !trustpilotState.isConnected
    ? 'disconnected'
    : trustpilotState.isActive
    ? 'active'
    : 'needs_reconnect';

  const isDirty =
    (integrationStatus !== 'active' || isEditing) &&
    !!(trustpilotState.clientId.value || trustpilotState.clientSecret.value || trustpilotState.businessUserId.value);

  const canSave =
    !trustpilotState.clientId.error &&
    !trustpilotState.clientSecret.error &&
    !trustpilotState.businessUserId.error &&
    !!trustpilotState.clientId.value &&
    !!trustpilotState.clientSecret.value;

  const onDiscard = () => {
    dispatch(
      settingsSlice.actions.handleChangeIntegrationTrustpilot({
        ...trustpilotState,
        clientId: { value: '', error: '' },
        clientSecret: { ...trustpilotState.clientSecret, value: '', error: '' },
        businessUserId: { value: '', error: '' },
      }),
    );
    setApiError((prev) => ({ ...prev, create: '', test: '' }));
    setTestConnectionResult(undefined);
    setIsEditing(false);
  };

  const onEdit = () => setIsEditing(true);

  const onToggleShowSecret = (key: string) => {
    if (key !== 'clientSecret') return;
    dispatch(
      settingsSlice.actions.handleChangeIntegrationTrustpilot({
        ...trustpilotState,
        clientSecret: { ...trustpilotState.clientSecret, isShow: !trustpilotState.clientSecret.isShow },
      }),
    );
  };

  const onFieldChange = (key: string, value: string) => {
    const error =
      key === 'clientSecret'
        ? validateSecretField(value)
        : key === 'businessUserId'
        ? validateOptionalIdentifierField(value)
        : validateIdentifierField(value);
    dispatch(
      settingsSlice.actions.handleChangeIntegrationTrustpilot({
        ...trustpilotState,
        [key]: { ...(trustpilotState as unknown as Record<string, { value: string; error: string }>)[key], value, error },
      }),
    );
    setApiError((prev) => ({ ...prev, create: '', test: '' }));
    setTestConnectionResult(undefined);
  };

  const onSave = () => {
    setApiError((prev) => ({ ...prev, create: '', test: '' }));
    createTrustpilot({
      clientId: trustpilotState.clientId.value,
      clientSecret: trustpilotState.clientSecret.value,
      businessUserId: trustpilotState.businessUserId.value || undefined,
    }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setApiError((prev) => ({ ...prev, create: '' }));
        setIsEditing(false);
        return;
      }
      setApiError((prev) => ({ ...prev, create: getIntegrationErrorMessage(res) }));
    });
  };

  const onTestConnection = () => {
    setApiError((prev) => ({ ...prev, create: '', test: '' }));
    testConnection({
      clientId: trustpilotState.clientId.value,
      clientSecret: trustpilotState.clientSecret.value,
      businessUserId: trustpilotState.businessUserId.value || undefined,
    }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1 && res.data.data) {
        setApiError((prev) => ({ ...prev, test: '' }));
        setTestConnectionResult(res.data.data);
        return;
      }
      setApiError((prev) => ({ ...prev, test: getIntegrationErrorMessage(res) }));
      setTestConnectionResult(undefined);
    });
  };

  const onDisconnect = () => {
    deleteTrustpilot().then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setApiError((prev) => ({ ...prev, delete: '' }));
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        dispatch(
          settingsSlice.actions.handleChangeIntegrationTrustpilot({
            ...trustpilotState,
            isConnected: false,
            isActive: true,
            isCollapse: false,
            businessUnitId: { value: '', error: '' },
            clientId: { value: '', error: '' },
            clientSecret: { value: '', error: '', isShow: false },
            businessUserId: { value: '', error: '' },
          }),
        );
        setTestConnectionResult(undefined);
        setIsEditing(false);
        return;
      }
      setApiError((prev) => ({ ...prev, delete: getIntegrationErrorMessage(res) }));
    });
  };

  useEffect(() => {
    if (isFetching) return;
    if (status) {
      dispatch(
        settingsSlice.actions.handleChangeIntegrationTrustpilot({
          ...trustpilotState,
          isConnected: true,
          isActive: status.isActive,
          businessUnitId: { ...trustpilotState.businessUnitId, value: status.businessUnitId },
        }),
      );
    } else {
      dispatch(
        settingsSlice.actions.handleChangeIntegrationTrustpilot({ ...trustpilotState, isConnected: false, isActive: true }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isFetching]);

  return {
    status: integrationStatus,
    isFetching,
    isSaving: createdTrustpilotData.isLoading,
    isDisconnecting: deletedTrustpilotData.isLoading,
    isTestingConnection: testedConnectionData.isLoading,
    testConnectionResult,
    fields: [
      { key: 'clientId', value: trustpilotState.clientId.value, error: trustpilotState.clientId.error },
      {
        key: 'clientSecret',
        value: trustpilotState.clientSecret.value,
        error: trustpilotState.clientSecret.error,
        isShow: trustpilotState.clientSecret.isShow,
      },
      { key: 'businessUserId', value: trustpilotState.businessUserId.value, error: trustpilotState.businessUserId.error },
    ],
    isDirty,
    canSave,
    apiError,
    isEditing,
    onEdit: integrationStatus === 'active' ? onEdit : undefined,
    connectedSummary:
      integrationStatus === 'active' ? { label: 'Business Unit ID:', value: trustpilotState.businessUnitId.value } : undefined,
    reconnectBanner:
      integrationStatus === 'needs_reconnect'
        ? {
            title: 'Reconnection required',
            message: 'Trustpilot has revoked access. Please reconnect to keep sending review invitations.',
          }
        : undefined,
    onFieldChange,
    onToggleShowSecret,
    onSave,
    onDiscard,
    onDisconnect,
    onTestConnection,
  };
};

export default useTrustpilotIntegrationAdapter;
